import session from 'frontend/shared/javascripts/util/session'
import getSafeLocalStorage from 'frontend/shared/javascripts/util/safe_local_storage'
import { postJson } from 'shared/javascripts/util/request'

// Per-user map of experiment_id -> last variation_id we successfully synced.
const SYNCED_KEY = 'studyflix.syncedExperimentAssignments'
// Guest-wide buffer of exposures seen before login, flushed once a user exists.
const BUFFER_KEY = 'studyflix.bufferedExperimentAssignments'
// Cap the buffer so a guest who never register/login cannot grow it without bound.
const MAX_BUFFERED = 50

interface ExperimentAssignment {
  experiment_id: string
  variation_id: string
  exposed_at: string
}

function syncedStorageKey(): string {
  return `${SYNCED_KEY}:${session.userId}`
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = getSafeLocalStorage().getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function readSyncedMap(): Record<string, string> {
  return readJson<Record<string, string>>(syncedStorageKey(), {})
}

function writeSyncedMap(map: Record<string, string>): void {
  getSafeLocalStorage().setItem(syncedStorageKey(), JSON.stringify(map))
}

function readBuffer(): ExperimentAssignment[] {
  const parsed = readJson<ExperimentAssignment[]>(BUFFER_KEY, [])
  return Array.isArray(parsed) ? parsed : []
}

function writeBuffer(assignments: ExperimentAssignment[]): void {
  getSafeLocalStorage().setItem(BUFFER_KEY, JSON.stringify(assignments))
}

function clearBuffer(): void {
  getSafeLocalStorage().removeItem(BUFFER_KEY)
}

function currentAssignments(): ExperimentAssignment[] {
  const experiences = window.convert?.currentData?.experiences ?? {}
  const exposedAt = new Date().toISOString()
  const assignments: ExperimentAssignment[] = []

  for (const [experimentId, experience] of Object.entries(experiences)) {
    const variationId = experience?.variation?.id
    if (variationId == null) continue

    assignments.push({
      experiment_id: String(experimentId),
      variation_id: String(variationId),
      exposed_at: exposedAt,
    })
  }

  return assignments
}

function combineByExperiment(
  earlier: ExperimentAssignment[],
  later: ExperimentAssignment[],
): ExperimentAssignment[] {
  const byExperiment = new Map<string, ExperimentAssignment>()
  for (const assignment of [...earlier, ...later]) {
    const previous = byExperiment.get(assignment.experiment_id)
    if (previous && previous.variation_id === assignment.variation_id) continue
    byExperiment.set(assignment.experiment_id, assignment)
  }
  return [...byExperiment.values()]
}

function bufferAssignments(current: ExperimentAssignment[]): void {
  if (current.length === 0) return

  writeBuffer(combineByExperiment(readBuffer(), current).slice(-MAX_BUFFERED))
}

let inFlight = false

export function syncExperimentAssignments(): void {
  const current = currentAssignments()

  if (!session.signedIn || session.userId == null) {
    bufferAssignments(current)
    return
  }

  if (inFlight) return

  const synced = readSyncedMap()
  const merged = combineByExperiment(readBuffer(), current)
  const pending = merged.filter(
    (assignment) => synced[assignment.experiment_id] !== assignment.variation_id,
  )
  if (pending.length === 0) {
    clearBuffer()
    return
  }

  inFlight = true
  void postJson('/experiment_assignments', { assignments: pending })
    .then((response) => {
      if (!response?.ok) return

      for (const assignment of pending) {
        synced[assignment.experiment_id] = assignment.variation_id
      }
      writeSyncedMap(synced)
      clearBuffer()
    })
    .finally(() => {
      inFlight = false
    })
}
